import { useAreaColor } from '../lib/area-color.js';
import { cn } from '../lib/cn.js';

/**
 * The base surface of the whole interface: blurred dark glass with a hairline
 * cyan edge (06-designsystem.md).
 */
export function GlassPanel({
  children,
  className,
  as: Tag = 'section',
  padded = true,
  accentColor,
  ...rest
}: {
  children: React.ReactNode;
  className?: string;
  as?: 'section' | 'div' | 'article' | 'aside';
  padded?: boolean;
  /** Area colour shown as a left edge, per the card spec. */
  accentColor?: string;
  /** Stable hook for the Playwright smoke tests. */
  'data-testid'?: string;
}) {
  const areaColor = useAreaColor();

  return (
    <Tag
      className={cn('glass shadow-panel relative overflow-hidden', padded && 'p-5', className)}
      {...rest}
    >
      {accentColor && (
        <span
          aria-hidden
          className="absolute inset-y-0 left-0 w-[3px]"
          style={{ backgroundColor: areaColor(accentColor) }}
        />
      )}
      {children}
    </Tag>
  );
}

export function PanelHeader({
  title,
  subtitle,
  action,
  icon,
}: {
  title: string;
  subtitle?: string;
  action?: React.ReactNode;
  icon?: React.ReactNode;
}) {
  return (
    <header className="mb-4 flex items-start justify-between gap-4">
      <div className="min-w-0">
        <div className="flex items-center gap-2">
          {icon && <span className="text-accent">{icon}</span>}
          <h2 className="font-display text-ink truncate text-base font-medium">{title}</h2>
        </div>
        {subtitle && <p className="text-ink-soft mt-1 text-sm">{subtitle}</p>}
      </div>
      {action && <div className="shrink-0">{action}</div>}
    </header>
  );
}
